import React, { useMemo, useRef, useEffect, useCallback } from "react";
import SVGraphics from "../../../assets/SVGraphics";
import { useViewer } from "../DicomViewerNew/context/ViewerContext";

const AnnotationMarkers = ({
  annotationData,
  currentImageIndex,
  viewport,
  isReady,
}) => {
  const { selectedAnnotationId, setSelectedAnnotationId, zoomToAnnotation } =
    useViewer();
  const containerRef = useRef(null);

  // Only annotations for the current slice
  const annsOnSlice = annotationData[currentImageIndex] || [];

  // Build markers purely from annotations (no nodulesData)
  const markers = useMemo(() => {
    return annsOnSlice
      .filter((a) => a.type !== "personal") // Ignore personal notes
      .map((a) => {
        let start = a.start,
          end = a.end;
        try {
          if (typeof start === "string") start = JSON.parse(start);
          if (typeof end === "string") end = JSON.parse(end);
        } catch (_) {}

        const sx = Array.isArray(start) ? start[0] : start?.x;
        const sy = Array.isArray(start) ? start[1] : start?.y;
        const ex = Array.isArray(end) ? end[0] : end?.x;
        const ey = Array.isArray(end) ? end[1] : end?.y;

        const mx = sx && ex ? (sx + ex) / 2 : a.x;
        const my = sy && ey ? (sy + ey) / 2 : a.y;

        return {
          id: a.id,
          type: "annotation",
          length: a.length,
          midpoint: { x: mx, y: my },
          // Anchor point (world coords) at the top-right of the orange region,
          // supplied by the overlay. When present the pin uses it so its corner
          // points at the segment without covering it; otherwise falls back to
          // the midpoint.
          anchor:
            typeof a.markerX === "number" && typeof a.markerY === "number"
              ? { x: a.markerX, y: a.markerY }
              : null,
        };
      });
  }, [annsOnSlice]);

  // Update marker positions on the canvas
  const updatePositions = useCallback(() => {
    if (!viewport || !containerRef.current) return;

    const canvas = viewport.canvas;
    if (!canvas) return;

    // Get canvas position relative to viewport element
    const viewportElement = viewport.element;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportRect = viewportElement.getBoundingClientRect();
    
    // Calculate offset of canvas within viewport
    const canvasOffsetX = canvasRect.left - viewportRect.left;
    const canvasOffsetY = canvasRect.top - viewportRect.top;

    // Update container position to match canvas
    containerRef.current.style.left = `${canvasOffsetX}px`;
    containerRef.current.style.top = `${canvasOffsetY}px`;
    containerRef.current.style.width = `${canvasRect.width}px`;
    containerRef.current.style.height = `${canvasRect.height}px`;

    markers.forEach((marker) => {
      const el = containerRef.current.querySelector(
        `[data-marker-id="${marker.id}"]`
      );
      if (!el) return;

      // Prefer the overlay-supplied anchor (top-right of the orange region);
      // fall back to the midpoint before the overlay has run.
      const point = marker.anchor || marker.midpoint;
      try {
        const [canvasX, canvasY] = viewport.worldToCanvas([point.x, point.y, 0]);
        if (!Number.isFinite(canvasX) || !Number.isFinite(canvasY)) {
          el.style.opacity = 0;
          return;
        }
        el.style.left = `${canvasX}px`;
        el.style.top = `${canvasY}px`;
        el.style.opacity = 1;
      } catch {
        el.style.opacity = 0;
      }
    });
  }, [viewport, markers]);

  useEffect(() => {
    if (!viewport || !containerRef.current) return;
    const elements = containerRef.current.querySelectorAll("[data-marker-id]");
    elements.forEach((el) => (el.style.opacity = 0));
    const id = requestAnimationFrame(() => updatePositions());
    return () => cancelAnimationFrame(id);
  }, [viewport, markers, updatePositions]);

  useEffect(() => {
    if (!viewport?.element) return;
    
    // Update positions on camera changes (pan, zoom)
    viewport.element.addEventListener(
      "CORNERSTONE_CAMERA_MODIFIED",
      updatePositions
    );
    
    // Also listen for window resize to update container position
    const handleResize = () => {
      updatePositions();
    };
    window.addEventListener("resize", handleResize);
    
    // Use ResizeObserver to watch for canvas size/position changes
    let resizeObserver = null;
    const canvas = viewport.canvas;
    if (canvas && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updatePositions();
      });
      resizeObserver.observe(canvas);
      resizeObserver.observe(viewport.element);
    }
    
    return () => {
      viewport.element.removeEventListener(
        "CORNERSTONE_CAMERA_MODIFIED",
        updatePositions
      );
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [viewport, updatePositions]);

  if (!isReady || !viewport) return null;

  return (
    <div 
      ref={containerRef} 
      data-annotation-container
      style={{
        position: "absolute",
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      {markers.map((m) => {
        const isSelected = m.id === selectedAnnotationId;

        return (
          <div
            key={m.id}
            data-marker-id={m.id}
            onClick={(e) => {
              e.stopPropagation();
              const isNowSelected = m.id !== selectedAnnotationId;
              setSelectedAnnotationId(isNowSelected ? m.id : null);
              zoomToAnnotation(isNowSelected ? m : null);
            }}
            style={{
              position: "absolute",
              opacity: 0,
              transition: "opacity 0.15s ease-out",
              width: 36,
              height: 36,
              background: isSelected ? "#5A3EFF" : "white",
              borderRadius: "50%",
              // Nudge the circle so the icon's pointer corner sits near the
              // anchor (top-right pixel of the segment) without overshooting.
              transform: "translate(-18%, -80%)",
              pointerEvents: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,.45)",
              zIndex: 49,
            }}
            title={
              typeof m.length === "number"
                ? `Length: ${m.length.toFixed(1)} mm`
                : "Annotation Marker"
            }>
            <SVGraphics
              svgname={
                isSelected
                  ? "MeasureAnnotationIconActive"
                  : "MeasureAnnotationIcon"
              }
              width="17px"
              height="17px"
            />
          </div>
        );
      })}
    </div>
  );
};

export default AnnotationMarkers;
